import { getLogger } from '@logtape/logtape';
import useSWR from 'swr';

import useConfig from '@/hooks/useConfig.ts';

import { DailyAppointment } from '@/models/appointment/Appointment.ts';
import { GetAppointmentsForTodayResponseDto } from '@/models/appointment/GetAppointmentsForTodayResponseDto.ts';

import { fetchAppointmentsForDate } from '@/services/appointmentService.ts';

import { useFilterStore } from '@/stores/filterStore.ts';
import { usePatientModalStore } from '@/stores/patientModalStore.ts';

import {
    enrichAppointmentsListWithIsEternoUser,
    mergeAppointments,
    removeCancelledAppointments,
} from '@/utils/appointmentUtils.ts';

const logger = getLogger(['hook', 'appointments-for-date']);

interface AppointmentsForDateOptions {
    show_cancelled?: boolean;
    show_cancelled_today?: boolean; // if removing cancelled appointment we can keep the one that are cancelled today
}

const useAppointmentsForDate = (
    options?: AppointmentsForDateOptions,
    is_active: boolean = true // this flag is used to disable the hook
): {
    appointments?: DailyAppointment[];
    is_loading: boolean;
    mutate: () => Promise<GetAppointmentsForTodayResponseDto | undefined>;
} => {
    const { customer_id, instance_ids, location_key } = useConfig();
    const selectedDate = useFilterStore((state) => state.date);

    const cacheKey = {
        key: 'appointments_for_date',
        date: selectedDate?.toLocaleDateString() ?? 'todays',
        customer_id: customer_id,
        instance_ids: instance_ids,
        location_key: location_key,
    };

    const fetchTodaysAppointmentData = async (
        instance_id: string,
        selected_date: Date | undefined
    ): Promise<GetAppointmentsForTodayResponseDto | undefined> => {
        try {
            const rawData: GetAppointmentsForTodayResponseDto = await fetchAppointmentsForDate(
                customer_id,
                instance_id,
                location_key,
                selected_date
            );

            return rawData;
        } catch (error) {
            logger.error('Failed to fetch appointments for the selected date', { error: error });
            return undefined;
        }
    };

    const fetcher = async (): Promise<GetAppointmentsForTodayResponseDto> => {
        const promises = instance_ids.map((instance_id) => fetchTodaysAppointmentData(instance_id, selectedDate));
        const responses = await Promise.all(promises);
        const filteredResponses: GetAppointmentsForTodayResponseDto[] = responses.filter((response) => !!response);

        const mergedAppointments = mergeAppointments(filteredResponses);

        // Temporary fix until backend is fixed
        enrichAppointmentsListWithIsEternoUser(mergedAppointments.data.active);
        enrichAppointmentsListWithIsEternoUser(mergedAppointments.data.inactive);

        return mergedAppointments;
    };

    const { data, isLoading, mutate } = useSWR<GetAppointmentsForTodayResponseDto>(
        is_active ? cacheKey : undefined,
        fetcher,
        {
            refreshInterval: 30000,
            dedupingInterval: 10000,
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: true,
            // Update selected appointment if it is in the response
            onSuccess: (data: GetAppointmentsForTodayResponseDto) => {
                const selectedAppointment = usePatientModalStore.getState().appointment;
                if (!selectedAppointment) return;

                const updatedAppointment =
                    data.data.active.find((apt) => apt.id === selectedAppointment.id) ??
                    data.data.inactive.find((apt) => apt.id === selectedAppointment.id);

                if (!updatedAppointment) return;

                usePatientModalStore.setState({ appointment: updatedAppointment });
            },
        }
    );

    // Active and Inactive matters only for Patient app
    const combinedAppointment: DailyAppointment[] = data ? [...data.data.active, ...data.data.inactive] : [];

    const filteredAppointments: DailyAppointment[] | undefined = options?.show_cancelled
        ? combinedAppointment
        : removeCancelledAppointments(combinedAppointment, options?.show_cancelled_today);

    return { appointments: filteredAppointments, is_loading: isLoading, mutate: mutate };
};

export default useAppointmentsForDate;
